import { loadSherpaTemplate } from '@/api/project';

const TEMPLATE_LOAD_CONCURRENCY = 5;

type TemplateLoader = (args: { projectId: number; data: { projectTemplateId: number } }) => Promise<unknown>;

export interface TemplateLoadFailure {
  projectId: number;
  error: unknown;
}

export async function loadProjectTemplatesInBatches(
  projectIds: number[],
  projectTemplateId: number,
  concurrency = TEMPLATE_LOAD_CONCURRENCY,
  loadTemplate: TemplateLoader = loadSherpaTemplate
): Promise<TemplateLoadFailure[]> {
  const failures: TemplateLoadFailure[] = [];

  for (let index = 0; index < projectIds.length; index += concurrency) {
    const batch = projectIds.slice(index, index + concurrency);
    const results = await Promise.allSettled(batch.map((projectId) => loadTemplate({ projectId, data: { projectTemplateId } })));

    results.forEach((result, resultIndex) => {
      if (result.status === 'rejected') {
        failures.push({ projectId: batch[resultIndex], error: result.reason });
      }
    });
  }

  return failures;
}

export async function createProjectWithOptionalTemplate<T extends { data: { id: number } }>(
  createProject: () => Promise<T>,
  projectTemplateId?: number,
  loadTemplate: TemplateLoader = loadSherpaTemplate
): Promise<{ project: T; templateLoadError: unknown | null }> {
  const project = await createProject();

  if (!projectTemplateId) {
    return { project, templateLoadError: null };
  }

  try {
    await loadTemplate({ projectId: project.data.id, data: { projectTemplateId } });
    return { project, templateLoadError: null };
  } catch (templateLoadError) {
    return { project, templateLoadError };
  }
}
